import React, { useState, useEffect } from "react";
import { Modal, Table, Tooltip } from "antd";
import { PrimaryButton, SecondaryButton } from "ui";
import TableWithDragableRows from "../../../molecules/table/draggable-table";
import { DragHandle } from "../../../molecules/table/draggable-table";
import {
  getCreateApptConfig,
  CreateApptConfigItem,
  saveCreateApptConfig,
  MUTUALLY_EXCLUSIVE_CONFIG_IDS,
} from "../../../utils/bridges/methods";
import { ReactComponent as CircleInfoIcon } from "../../../assets/icons/font-awesome/circle-info-regular.svg";
import queryClient from "../../../instances/react-query-client";
import { getAllQueuesQuery } from "../../../apis/tryst/get-all-queues";
import { CREATE_APPT_CONFIG_QUERY_KEY } from "../../../constants/query-keys";
import { SUBJECT_KEYS } from "@elixir/types";
import { getAclViewClassName } from "@elixir/utils";

const ACL_KEY_MAP: Partial<Record<CreateApptConfigItem['id'], SUBJECT_KEYS>> = {
  abha: SUBJECT_KEYS.ABHA,
  treatmentPackage: SUBJECT_KEYS.TREATMENT,
  auto_create_receipt_on_services: SUBJECT_KEYS.CAN_CREATE_RECEIPT_WITH_APT,
};

interface ApptConfigEditModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (config: CreateApptConfigItem[]) => void;
}

const MUTUALLY_EXCLUSIVE_SET = new Set<string>(MUTUALLY_EXCLUSIVE_CONFIG_IDS);

const ApptConfigEditModal: React.FC<ApptConfigEditModalProps> = ({
  open,
  onClose,
  onSave,
}) => {
  const [editedConfig, setEditedConfig] = useState<CreateApptConfigItem[]>([]);
  const [initialConfig, setInitialConfig] = useState<CreateApptConfigItem[]>(
    []
  );

  useEffect(() => {
    if (open) {
      const loadConfig = async () => {
        const config = await getCreateApptConfig();
        setInitialConfig(config);
        setEditedConfig([...config]);
      };
      loadConfig();
    }
  }, [open]);

  const handleSaveConfig = async () => {
    setInitialConfig(editedConfig);
    await saveCreateApptConfig(editedConfig);
    onSave(editedConfig);
    queryClient.invalidateQueries(getAllQueuesQuery({}));
    queryClient.invalidateQueries({ queryKey: [CREATE_APPT_CONFIG_QUERY_KEY] });
    onClose();
  };

  const handleCancelConfig = () => {
    setEditedConfig(initialConfig);
    onClose();
  };

  const handleToggleField = (id: string) => {
    setEditedConfig((prev) => {
      const targetItem = prev.find((item) => item.id === id);
      if (!targetItem) return prev;

      const willBeShown = !targetItem.isShown;

      // when and if enabling a mutually exclusive config [things which cant co exist], disable the others
      if (willBeShown && MUTUALLY_EXCLUSIVE_SET.has(id)) {
        return prev.map((item) => {
          if (item.id === id) {
            return { ...item, isShown: true };
          }
          if (MUTUALLY_EXCLUSIVE_SET.has(item.id) && item.id !== id) {
            return { ...item, isShown: false };
          }
          return item;
        });
      }

      return prev.map((item) =>
        item.id === id ? { ...item, isShown: willBeShown } : item
      );
    });
  };

  const findIndexForRecordKey = (id: string): number => {
    return editedConfig.findIndex((item) => item.id === id);
  };

  const handleSortEnd = ({
    oldIndex,
    newIndex,
  }: {
    oldIndex: number;
    newIndex: number;
  }) => {
    const newConfig = [...editedConfig];
    const [removed] = newConfig.splice(oldIndex, 1);
    newConfig.splice(newIndex, 0, removed);
    setEditedConfig(newConfig);
  };

  return (
    <Modal
      title={<p className="dr-font-700">Configure Appointment Fields</p>}
      open={open}
      onCancel={handleCancelConfig}
      footer={
        <div className="dr-flex dr-justify-between">
          <SecondaryButton title="Cancel" onClick={handleCancelConfig} />
          <PrimaryButton title="Save" onClick={handleSaveConfig} />
        </div>
      }
      width={500}
    >
      <TableWithDragableRows
        dataSource={editedConfig}
        findIndexForRecordKey={findIndexForRecordKey}
        onSortEnd={handleSortEnd}
        className=""
        rowClassName={(record) => `${ACL_KEY_MAP[record.id] ? ` ${getAclViewClassName(ACL_KEY_MAP[record.id] as SUBJECT_KEYS)}` : ''}`}
        pagination={false}
      >
        <Table.Column
          title=""
          key="drag"
          width={50}
          render={() => <DragHandle />}
        />
        <Table.Column
          title="Field"
          dataIndex="name"
          key="name"
          render={(name: string, record: CreateApptConfigItem) => (
            <div className="dr-flex dr-items-center dr-space-x-6">
              <span className="dr-font-700">{name}</span>
              {record.icon && (
                <Tooltip
                  title={
                    "Only one of these can be enabled at a time. If you turn on 'Create Quick Receipt', 'Payment Status' will be disabled, and vice-versa"
                  }
                  overlayStyle={{ maxWidth: 280 }}
                >
                  <span className="dr-flex dr-items-center dr-cursor-pointer">
                    <CircleInfoIcon className="dr-w-14 dr-h-14 dr-text-darwin-neutral-600" />
                  </span>
                </Tooltip>
              )}
            </div>
          )}
        />
        <Table.Column
          title="Enable/Disable"
          key="isShown"
          width={150}
          align="center"
          render={(_: unknown, record: CreateApptConfigItem) => (
            <div
              onClick={() => handleToggleField(record.id)}
              style={{
                cursor: "pointer",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              {record.isShown ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 448 512"
                  className="w-16 h-16 fill-status-success"
                >
                  <path d="M384 32C419.3 32 448 60.65 448 96V416C448 451.3 419.3 480 384 480H64C28.65 480 0 451.3 0 416V96C0 60.65 28.65 32 64 32H384zM339.8 211.8C350.7 200.9 350.7 183.1 339.8 172.2C328.9 161.3 311.1 161.3 300.2 172.2L192 280.4L147.8 236.2C136.9 225.3 119.1 225.3 108.2 236.2C97.27 247.1 97.27 264.9 108.2 275.8L172.2 339.8C183.1 350.7 200.9 350.7 211.8 339.8L339.8 211.8z"></path>
                </svg>
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 448 512"
                  className="w-16 h-16 fill-neutral-6"
                >
                  <path d="M384 32C419.3 32 448 60.65 448 96V416C448 451.3 419.3 480 384 480H64C28.65 480 0 451.3 0 416V96C0 60.65 28.65 32 64 32H384zM384 80H64C55.16 80 48 87.16 48 96V416C48 424.8 55.16 432 64 432H384C392.8 432 400 424.8 400 416V96C400 87.16 392.8 80 384 80z"></path>
                </svg>
              )}
            </div>
          )}
        />
      </TableWithDragableRows>
    </Modal>
  );
};

export default ApptConfigEditModal;
